<template>
  <div class="pb-beams-selector">
    <BeamsGrid
      mode="select"
      :columns-count="occupied.columns"
      :rows-count="occupied.rows"
      :beams="beams"
      :loading="loading"
      :disabled="disabled"
      @change="toggleBeamSelect"
    />
    <div class="pb-beams-actions">
      <PbInput
        :label="translate('uiKit.beamsEdition.allBeams')"
        :type="INPUT_TYPE_SWITCH"
        :value="allSelected"
        :disabled="loading || disabled"
        @input="toggleAll"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// name: 'BeamsSelector'

import { computed, toRefs } from 'vue'

import { IDLE } from '@nicolaudie-group/ng-ui-kit/helpers/status'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'
import { INPUT_TYPE_SWITCH } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'

import PbInput from '@/components/PbInput.vue'

import { beamHelpers } from '@/helpers/profile'

import useTranslate from '@/composables/useTranslate'
import { useBeams } from '@/composables/useProfile'

import BeamsGrid from './BeamsGrid.vue'
import type { Cell, ChannelBeam } from './beamsTypes.ts'

interface Props {
  /**
   * Status of the requests related to the event emitted by this component
   */
  // TODO: infer from UI kit "status" helper
  status?: 'SUCCESS' |'ERROR' |'PENDING' |'IDLE'
  /**
   * Disable the beams selection
   */
  disabled?: boolean,
}

const props = withDefaults(defineProps<Props>(), {
  status: IDLE,
  disabled: false,
})

/**
 * Selected beams
 */
const selectedBeams = defineModel<ChannelBeam[]>('selection', {
  default: (): ChannelBeam[] => [],
})

const { translate } = useTranslate()
const { status } = toRefs(props)
const { pending: loading } = useStatus(status)

function fallback<V>(value: V[] | undefined): V[] {
  return value || []
}

const originalBeams = useBeams()

/**
 * Gets the beams of the profile, and add a "selected" flag on them
 * if their "id" matches a "beam_index" in the provided list of selected
 * beams.
 *
 * Sets the list of selected beams based on the "selected" flag from
 * the list of beams of the profiles returned by the getter
 */
const beams = computed({
  get() {
    const profileBeams = fallback(originalBeams.value)
    const selectedIds = fallback(selectedBeams.value).map((beam) => beam.beam_index)

    return profileBeams.map((beam) => {
      const selected = !selectedIds.length || selectedIds.includes(beam.id)
      // If there is only one beam selected, disable it so that it can't be deselected
      const disabled = selected && selectedIds.length === 1
      return {
        ...beam,
        selected,
        disabled,
      }
    })
  },
  set(beamsWithSelected) {
    if (!beamsWithSelected) {
      selectedBeams.value = beamsWithSelected
      return
    }

    const newSelectedBeams: ChannelBeam[] = []
    let allSelected = true
    for (let i = 0; i < beamsWithSelected.length; i += 1) {
      const beamWithSelected = beamsWithSelected[i]
      if (beamWithSelected?.selected) {
        newSelectedBeams.push({ beam_index: beamWithSelected.id })
      } else {
        allSelected = false
      }
    }
    selectedBeams.value = allSelected ? [] : newSelectedBeams
  }
})

/**
 * Wether all beams in the profile are selected for this channel
 */
const allSelected = computed(() => {
  const checkedBeams = beams.value.filter((beam) => beam.selected)
  return !checkedBeams.length || checkedBeams.length === beams.value.length
})

// Count the occupied rows & columns
const occupied = computed(() => beamHelpers.countOccupied(beams.value))

/**
 * Toggle select flag on one beam
 * @param cell - Cell containing beam to toggle, toggle value
 */
function toggleBeamSelect(cell: Cell, cellChecked: boolean) {
  // Get the beam of the cell
  const clickedBeam = cell.beam
  // Update the beams array
  beams.value = beams.value.map((beam) => {
    // For each beam
    if (beam.id === clickedBeam?.id) {
      // If the current beam is the beam of
      // the cell clicked, set the "selected" flag
      return {
        ...beam,
        selected: cellChecked,
      }
    }
    // If the clickedBeam is not the current beam,
    // return it untouched
    return beam
  })
}

/**
 * Toggle select flag on all beams (except the first one when deselecting)
 */
function toggleAll() {
  if (allSelected.value) {
    // If all beams are already selected,
    // unselect all beams except the first one
    beams.value = beams.value.map((beam, idx) => ({
      ...beam,
      selected: idx === 0,
    }))
  } else {
    // If not all beams are selected, select all of them
    // An empty array is a shortcut for all beams
    beams.value = []
  }
}

</script>
